import type { IDataRoomResumeRecord } from './types';

export class DataRoomResumeRecordHelper {
  private static readonly RESUME_KEY_PREFIX = 'commenda-ai:data-room-upload:';

  static resumeStorageKey(conversationId: string): string {
    return `${DataRoomResumeRecordHelper.RESUME_KEY_PREFIX}${conversationId}`;
  }

  /** A room dropped before its chat exists belongs to the workspace's next new chat until it is sent. */
  static newChatStorageKey(companyId: number | null): string {
    return `${DataRoomResumeRecordHelper.RESUME_KEY_PREFIX}new-chat:${companyId ?? 'none'}`;
  }

  static parseResumeRecord(raw: string | null): IDataRoomResumeRecord | null {
    if (!raw) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null) return null;
      // Safe: a non-null object, and every field below is type-checked before use.
      const { uploadId, fileCount, lastModifiedByBatch, removedFileIds } =
        parsed as Record<string, unknown>;
      if (typeof uploadId !== 'string' || typeof fileCount !== 'number')
        return null;
      const batches = Array.isArray(lastModifiedByBatch)
        ? lastModifiedByBatch.map((batch: unknown) =>
            Array.isArray(batch)
              ? batch.filter(
                  (value): value is number => typeof value === 'number'
                )
              : []
          )
        : [];
      return {
        uploadId,
        fileCount,
        lastModifiedByBatch: batches,
        removedFileIds: Array.isArray(removedFileIds)
          ? removedFileIds.filter(
              (value): value is string => typeof value === 'string'
            )
          : [],
      };
    } catch {
      return null;
    }
  }

  /** A drop counts its whole size when it starts, so a reload mid-add shows the room as unfinished. */
  static withDrop(
    record: Readonly<IDataRoomResumeRecord>,
    fileCount: number
  ): IDataRoomResumeRecord {
    return { ...record, fileCount: record.fileCount + fileCount };
  }

  static withBatch(
    record: Readonly<IDataRoomResumeRecord>,
    batchIndex: number,
    lastModified: readonly number[]
  ): IDataRoomResumeRecord {
    const lastModifiedByBatch = Array.from(
      { length: Math.max(record.lastModifiedByBatch.length, batchIndex + 1) },
      (_, index) => record.lastModifiedByBatch[index] ?? []
    );
    lastModifiedByBatch[batchIndex] = [...lastModified];
    return { ...record, lastModifiedByBatch };
  }

  static withRemovedFile(
    record: Readonly<IDataRoomResumeRecord>,
    fileId: string
  ): IDataRoomResumeRecord {
    return record.removedFileIds.includes(fileId)
      ? { ...record }
      : { ...record, removedFileIds: [...record.removedFileIds, fileId] };
  }

  static withoutRemovedFile(
    record: Readonly<IDataRoomResumeRecord>,
    fileId: string
  ): IDataRoomResumeRecord {
    return {
      ...record,
      removedFileIds: record.removedFileIds.filter((id) => id !== fileId),
    };
  }

  /** The files a resume still expects: every file added minus those removed. */
  static expectedFileCount(record: Readonly<IDataRoomResumeRecord>): number {
    return record.fileCount - record.removedFileIds.length;
  }

  static readResumeRecord(storageKey: string): IDataRoomResumeRecord | null {
    if (typeof window === 'undefined') return null;
    return DataRoomResumeRecordHelper.parseResumeRecord(
      window.sessionStorage.getItem(storageKey)
    );
  }

  /** Falls back to a record without last-modified times when sessionStorage is full; resume then
   * pairs files by name and size only. */
  static saveResumeRecord(
    storageKey: string,
    record: Readonly<IDataRoomResumeRecord>
  ): void {
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(record));
    } catch {
      try {
        window.sessionStorage.setItem(
          storageKey,
          JSON.stringify({ ...record, lastModifiedByBatch: [] })
        );
      } catch {
        // Without storage the upload still finishes in this tab; only a reload cannot resume it.
      }
    }
  }

  static clearResumeRecord(storageKey: string): void {
    window.sessionStorage.removeItem(storageKey);
  }
}
